import React, { useReducer, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import JoyRide, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { SideBarItem, Button, Card, CardBody } from '@kudi-inc/dip';
import { connect } from 'react-redux';

import { Option } from 'assets/svgs';
import styles from './layout.module.scss';

const TOUR_STEPS = [
  {
    target: '.tour-wallet-history',
    title: (
      <p>
        View your wallet history{' '}
        <span role="img" aria-label="emoji">
          🗂
        </span>
      </p>
    ),
    content:
      'Click the wallet history tab to view all payments made in and out of your wallet',
    placement: 'right',
    placementBeacon: 'right',
    disableBeacon: true, // This makes the tour to start automatically without clicking
  },
  {
    target: '.tour-dashboard',
    title: (
      <p>
        View your Dashboard{' '}
        <span role="img" aria-label="emoji">
          📊
        </span>
      </p>
    ),
    content:
      'Click the dashboard tab to view your wallet balance, collections summary',
    placement: 'right',
    placementBeacon: 'right',
    disableBeacon: true, // This makes the tour to start automatically without clicking
  },
  {
    target: '.tour-products',
    title: (
      <p>
        Create your first product{' '}
        <span role="img" aria-label="emoji">
          🗂
        </span>
      </p>
    ),
    content:
      'Click the products tab to view your products or create a new one.',
    placement: 'right',
    placementBeacon: 'right',
  },
  {
    target: '.tour-settlement',
    title: (
      <p>
        View your settlements{' '}
        <span role="img" aria-label="emoji">
          🗂
        </span>
      </p>
    ),
    content:
      'Click the settlement tab to view all collections payments settled to your designated bank account',
    placement: 'right',
    placementBeacon: 'right',
  },
  {
    target: '.tour-settings',
    title: (
      <p>
        Manage your account{' '}
        <span role="img" aria-label="emoji">
          🛠
        </span>
      </p>
    ),
    content:
      'Click the settings tab to add team members, update business details, set settlement bank account, set settlement time, change your passwords and set authentication PIN',
    placement: 'right',
    placementBeacon: 'right',
  },
];

const INITIAL_STATE = {
  key: new Date(), // This field makes the tour to re-render when we restart the tour
  run: false,
  continuous: true,
  loading: false,
  stepIndex: 0,
  steps: TOUR_STEPS,
};
// Reducer will manage updating the local state
const tourReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case 'START':
      return { ...state, run: true };
    case 'RESET':
      return { ...state, stepIndex: 0 };
    case 'STOP':
      return { ...state, run: false };
    case 'NEXT_OR_PREV':
      return { ...state, ...action.payload };
    case 'RESTART':
      return {
        ...state,
        stepIndex: 0,
        run: true,
        loading: false,
        key: new Date(),
      };
    default:
      return state;
  }
};

const Tour = ({ user }) => {
  // Tour state is the state which control the JoyRide component
  const [tourState, dispatch] = useReducer(tourReducer, INITIAL_STATE);

  // if the tour is not viewed before
  const [checker, setChecker] = useState(
    !localStorage.getItem('productTourGuide')
  );

  const [isFirstTime, setFirstTime] = useState(false);

  const history = useHistory();

  const goToSettings = () => {
    localStorage.setItem('productTourGuide', '1');
    setFirstTime(false);
    user.merchantUser.isFirstTimerUser = false;

    history.push({
      pathname: '/settings',
      state: {
        settingsMenu: 'Settlement',
      },
    });
  };

  useEffect(() => {
    if (
      user &&
      user.status === 'ACTIVE' &&
      user?.merchantUser.role === 'OWNER' &&
      user?.merchantUser?.isFirstTimerUser
    ) {
      setFirstTime(true);
    }
  }, []);

  // Set once tour is viewed, skipped or closed
  const setTourViewed = () => {
    localStorage.setItem('productTourGuide', '1');
    setChecker(false);
  };

  const callback = (data) => {
    const { action, index, type, status } = data;

    if (
      // If close button clicked, then close the tour
      action === ACTIONS.CLOSE ||
      // If skipped or end tour, then close the tour
      (status === STATUS.SKIPPED && tourState.run) ||
      status === STATUS.FINISHED
    ) {
      setTourViewed();
      dispatch({ type: 'STOP' });
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Check whether next or back button click and update the step.
      dispatch({
        type: 'NEXT_OR_PREV',
        payload: { stepIndex: index + (action === ACTIONS.PREV ? -1 : 1) },
      });
    }
  };

  const startTour = () => {
    // Start the tour manually
    dispatch({ type: 'RESTART' });
  };
  return (
    <>
      <SideBarItem
        className={styles.links}
        icon={<Option />}
        text="Tour Guide"
        active={false}
        onClick={startTour}
      />
      <JoyRide
        {...tourState}
        callback={callback}
        disableCloseOnEsc={true}
        styles={{
          tooltipContainer: {
            textAlign: 'left',
          },
          buttonBack: {
            marginRight: 10,
            backgroundColor: '#18379A',
            color: '#fff',
          },
          buttonNext: {
            marginRight: 10,
            backgroundColor: '#fff',
            color: '#18379A',
          },
          tooltip: {
            borderRadius: 0,
            fontSize: 14,
            padding: 20,
          },
          tooltipTitle: {
            marginLeft: 10,
            marginTop: 5,
            marginBottom: 0,
            fontSize: 14,
            fontWeight: 'bold',
          },
          options: {
            arrowColor: '#18379A',
            borderRadius: 0,
            backgroundColor: '#18379A',
            overlayColor: 'rgba(0, 0, 0, 0.4)',
            primaryColor: '#000',
            textColor: '#fff',
            zIndex: 1000,
          },
        }}
        locale={{
          last: 'End Tour',
        }}
      />

      {isFirstTime ? (
        <div className={styles.kycCardWrapper}>
          <Card className={styles.kycCard}>
            <CardBody>
              <h3 className={styles.kycCard__Header}>
                Welcome to Dash - Complete Account Setup
              </h3>
              <p className={styles.kycCard__Text}>
                Please complete your account setup by setting your settlement
                time, settlement bank account details, security PIN and KYC
                details
              </p>
              <div className={styles.kycCard__Btn_Wrapper}>
                <Button
                  // loading={inProgress}
                  type="submit"
                  className={styles.kycCard__Button}
                  onClick={goToSettings}
                >
                  Go To Settings
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}
    </>
  );
};

const mapStateToProps = ({ common: { user } }) => ({
  user,
});

export default connect(mapStateToProps)(Tour);
